import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { READING_REGISTRY, deepClone } from "@/data/contentRegistry";
import { ContentEditor } from "@/components/admin/JsonNodeEditor";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, BookOpen, Pencil } from "lucide-react";

export default function AdminReading() {
  const { refresh } = useContent();
  const [selected, setSelected] = useState(READING_REGISTRY[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overridden, setOverridden] = useState({});
  const [confirmReset, setConfirmReset] = useState(false);

  const reg = useMemo(() => READING_REGISTRY.find((r) => r.key === selected), [selected]);

  const loadMeta = () => {
    api.get("/api/admin/content").then(({ data }) => {
      const map = {};
      (data.overrides || []).forEach((o) => { if (o.kind === "reading") map[o.key] = o.updated_at; });
      setOverridden(map);
    }).catch(() => {});
  };

  const loadDoc = (key) => {
    setLoading(true);
    api.get(`/api/admin/content/reading/${key}`).then(({ data }) => {
      const r = READING_REGISTRY.find((x) => x.key === key);
      setData(data.data != null ? data.data : deepClone(r.defaults));
    }).catch(() => {
      const r = READING_REGISTRY.find((x) => x.key === key);
      setData(deepClone(r.defaults));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { loadDoc(selected); }, [selected]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/content/reading/${selected}`, { data });
      toast.success(`Saved ${reg.label} references. Now live for all trainees.`);
      loadMeta();
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setConfirmReset(false);
    try {
      await api.delete(`/api/admin/content/reading/${selected}`);
      toast.success(`${reg.label} references reset to default.`);
      setData(deepClone(reg.defaults));
      loadMeta();
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const linkCount = data && Array.isArray(data.links) ? data.links.length : 0;

  return (
    <div data-testid="admin-reading-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold mb-1">
        <BookOpen className="w-3.5 h-3.5" /> Further reading
      </div>
      <h1 className="text-2xl font-bold text-white">Reference links</h1>
      <p className="text-sm text-slate-400 mt-1">Edit the intro blurb and the curated links shown on each simulator's debrief.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-slate-800 bg-[#0e1726] p-2 space-y-1" data-testid="admin-reading-list">
            {READING_REGISTRY.map((r) => (
              <button
                key={r.key}
                onClick={() => setSelected(r.key)}
                data-testid={`admin-reading-${r.key}`}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center justify-between gap-2 ${selected === r.key ? "bg-[#0047AB] text-white" : "text-slate-300 hover:bg-slate-800"}`}
              >
                <span className="text-sm font-medium truncate">{r.label}</span>
                {overridden[r.key] && <Pencil className="w-3 h-3 text-amber-400 shrink-0" title="Edited" />}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-xl border border-slate-800 bg-white">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 flex-wrap">
              <div>
                <div className="text-sm font-semibold text-slate-900">{reg?.label}</div>
                <div className="text-[11px] text-slate-500">{linkCount} link{linkCount === 1 ? "" : "s"}{overridden[selected] ? " · customised" : " · default"}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={!overridden[selected]} onClick={() => setConfirmReset(true)} data-testid="admin-reading-reset" className="text-slate-600 text-xs">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to default
                </Button>
                <Button size="sm" disabled={saving || loading} onClick={save} data-testid="admin-reading-save" className="bg-[#0047AB] hover:bg-[#003380] text-white text-xs">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save changes
                </Button>
              </div>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {loading || data == null ? (
                <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-16">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading references…
                </div>
              ) : (
                <ContentEditor data={data} onChange={setData} />
              )}
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent data-testid="admin-reading-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to built-in default?</AlertDialogTitle>
            <AlertDialogDescription>
              This discards all custom edits for <strong>{reg?.label}</strong> references and restores the original links.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-reading-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reset} data-testid="admin-reading-reset-confirm" className="bg-rose-600 hover:bg-rose-700">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
